<script setup lang="ts">
import type { PreSignedFile } from '~/models/Media'
import type { GroupMember } from '~/models/Group'
import type { Form } from '~/models/Content/Form'
import type { ContentModelText } from '~/models/Content/ContentModelText'
import type { ContentImage } from '~/models/Content/ContentImage'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import type { ImageRelationField } from '~/models/Content/BaseField'
import type { AssignmentAnswer, AssignmentAnswerData } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, inject, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { debounce } from '~/utils/functionUtils'
import arrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { Subtree } from '~/models/Content/Subtree'
import useSlugify from '~/composables/useSlugify'
import { key } from '~/composables/useOverlayOutlet'
import { useMedia } from '~/composables/useMedia'
import useImage from '~/composables/useImage'
import useFormWriter from '~/composables/useFormWriter'
import { formatSeconds } from '~/composables/useAudioRecorder'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Select from '~/components/ui/Select.vue'
import Button from '~/components/ui/Button.vue'
import AuCallout from '~/components/ui/AuCallout.vue'
import FormWriterSkeleton from '~/components/skeletons/FormWriterSkeleton.vue'
import FileUploader from '~/components/media/FileUploader.vue'
import AudioRecorder from '~/components/media/AudioRecorder.vue'
import ToolBar from '~/components/formwriter/components/ToolBar.vue'
import StudentFilters from '~/components/formwriter/components/StudentFilters.vue'
import InputField from '~/components/formwriter/components/InputField.vue'
import ImageUpload from '~/components/formwriter/components/ImageUpload.vue'
import ImagesBar from '~/components/formwriter/components/ImagesBar.vue'
import ExportButton from '~/components/formwriter/components/ExportButton.vue'
import ModelText from '~/components/article/ModelText.vue'

const {
  item,
  observerMode = false,
  preSelectedStudent
} = defineProps<{
  item: ContentFormWriter
  observerMode?: boolean
  preSelectedStudent?: GroupMember
}>()

const { t } = useI18n()
const { getImageSrc } = useImage()
const { findContents } = useContentApi()
const { userId, isTeacher, isStudent, isAuthenticated } = storeToRefs(useAuthStore())
const { slugify } = useSlugify()
const { truthy } = arrayUtils()
const { themeColor } = useAppColor()

const images = ref<string[]>([])
const showPlaceholder = ref(true)
const showDescription = ref(true)
const showLabel = ref(true)
const showModelText = ref(false)
const activeStudent = ref<GroupMember>()
const overlayOutletHeight = inject(key, ref(0))

const selectedVersionId = ref<number>()
const justSaved = ref(false)
const initialFormData = ref<AssignmentAnswerData>()
const currentFormData = ref<AssignmentAnswerData>({
  image: undefined,
  fields: [],
  audio: undefined,
  file: undefined,
})

watch(() => preSelectedStudent, (student) => {
  if (!student) {
    return
  }
  activeStudent.value = student
}, { immediate: true })

const currentUserId = computed(() => {
  if (isTeacher.value && activeStudent.value) {
    return activeStudent.value.userId
  } else {
    return userId.value
  }
})

const {
  data,
  isLoading: isLoadingFields,
  isError
} = useQuery({
  queryKey: ['form_writer_fields', item.locationId, item.form.destinationContentId],
  queryFn: async () => (await findContents<Form>({
    contentIdCriterion: [Number(item.form.destinationContentId)],
    subtreeCriterion: [Subtree.Forms, Subtree.Content]
  }))[0],
  enabled: !!item.form.destinationContentId,
  staleTime: Infinity
})

const {
  data: modelText,
  isLoading: isLoadingModelText,
} = useQuery({
  queryKey: ['form_writer_model_text', item.locationId, item.modelText.destinationContentId],
  queryFn: async () => (await findContents<ContentModelText>({
    contentIdCriterion: [Number(item.modelText.destinationContentId)],
    subtreeCriterion: [Subtree.Media, Subtree.Content]
  }))[0],
  enabled: computed(() => !!item.modelText.destinationContentId && isAuthenticated.value),
  staleTime: Infinity
})

const {
  data: imagesOnCanvas,
  isFetching
} = useMedia(item.imagesOnCanvas.destinationContentIds ?? [])

const {
  assignment,
  metadata,
  saveAssignment,
  updateForm,
  isLoadingFormData,
} = useFormWriter(item.locationId, currentUserId, item.assignment, true)

const versions = computed(() => {
  const answers = assignment.value?.answer || []
  if (!answers.length)
    return []
  if (isStudent.value)
    return answers
  // To avoid duplicate versions when students press the CTA button
  const unique = answers.length > 1 && answers[0].data === answers[1].data
    ? answers.slice(1)
    : answers
  return unique.filter(answer => !isTeacher.value || !answer.isDraft)
})

const currentVersion = computed(() => selectedVersionId.value !== undefined
  ? versions.value.find(v => v.version === selectedVersionId.value)
  : undefined)

const versionOptions = computed(() => versions.value
  .map(v => v.version)
  .filter((v): v is number => v !== undefined))

const previousVersion = computed(() => assignment.value?.answer.find(({ isDraft }) => !isDraft))

const formData = computed(() => {
  if (!item.assignment && metadata.value) {
    return metadata.value
  }
  if (currentVersion.value) {
    return JSON.parse(currentVersion.value.data)
  }
  if (metadata.value && item.assignment) {
    if (!assignment.value || assignment.value?.answer.length === 0) {
      return metadata.value
    }
  }
  return null
})

const isViewingPreviousVersion = computed(() => {
  if (!isStudent.value || !versions.value.length || !currentVersion.value) {
    return false
  }
  return currentVersion.value.version !== versions.value[0]?.version
})

const canUseInputs = computed(() => isStudent.value || (isTeacher.value && (activeStudent.value || !assignment.value)))

const canEditInputs = computed(() => {
  if (isStudent.value && isViewingPreviousVersion.value) {
    return false
  }
  return isStudent.value || (isTeacher.value && !activeStudent.value)
})

const hasFormInput = computed(() => (currentFormData.value.fields ?? []).filter(truthy<string>).length > 0)

const isLoading = computed(() => isLoadingFields.value || isLoadingFormData.value || isLoadingModelText.value)
const showFormFields = computed(() => item.methods.includes('form_fields') && !!data?.value?.fields.length && !isLoading.value && !showModelText.value)
const showFileUpload = computed(() => item.methods.includes('file_upload') && !isLoading.value && !showModelText.value)
const showAudioRecorder = computed(() => item.methods.includes('audio_recording') && !isLoading.value && !showModelText.value)
const showImageUpload = computed(() => (item.methods.includes('image_upload') || item.imagesOnCanvas.destinationContentIds.length) && !isLoading.value)
const imageUploadDisabled = computed(() => !item.methods.includes('image_upload') && !isLoading.value)
const audioName = computed(() => `rec-${item.locationId}-${slugify(item.title)}`)

const canCreateNewVersion = computed(() => {
  if (!item.assignment || !currentVersion.value || isLoadingFormData.value || saveAssignment.isPending.value)
    return false

  if (isStudent.value && isViewingPreviousVersion.value)
    return false

  const hasNewChanges = JSON.stringify(currentFormData.value) !== JSON.stringify(initialFormData.value)
  const pendingChanges = JSON.stringify(currentVersion.value.data) !== JSON.stringify(previousVersion.value?.data)
  return pendingChanges || hasNewChanges
})

const shouldAutoSelectVersion = computed(() => isStudent.value || (isTeacher.value && activeStudent.value))

const showStudentFilters = computed(() => (isTeacher.value && item.assignment) && !observerMode)
const showToolBar = computed(() => (showFormFields.value || showModelText.value) && !observerMode)
const showImagesBar = computed(() => (imagesOnCanvas.value.length && !isFetching.value && canUseInputs.value) && !observerMode)
const showSubmitSection = computed(() => !showModelText.value && item.assignment && !isTeacher.value && !observerMode)

const debouncedUpdateForm = debounce((formData: AssignmentAnswerData) => updateForm(formData), 1000)

const onInput = (index: number, value: string) => {
  if (undefined !== currentFormData.value.fields) {
    currentFormData.value.fields[index] = value
  }
  debouncedUpdateForm(currentFormData.value)
}

const onSubmit = () => {
  if (!currentFormData.value)
    return
  updateForm(currentFormData.value)
}

const saveCurrentVersion = async () => {
  if (!currentVersion.value) {
    console.warn('No current version selected for saving')
    return
  }

  try {
    justSaved.value = true
    await saveAssignment.mutateAsync({
      ...currentVersion.value,
      data: JSON.stringify(currentFormData.value)
    })
    initialFormData.value = { ...currentFormData.value }
  } catch (error) {
    justSaved.value = false
    console.error('Failed to save version:', error)
    throw error
  }
}

const resetCurrentFormData = () => {
  currentFormData.value = {
    image: undefined,
    fields: [],
    audio: undefined,
    file: undefined,
  }
  initialFormData.value = { ...currentFormData.value }
}

const saveImageUpload = (src: string) => {
  currentFormData.value.image = src
  if (canEditInputs.value) {
    images.value = [src, ...images.value]
    updateForm(currentFormData.value)
  }
}

const saveFileUpload = (file: PreSignedFile) => {
  if (!file)
    return
  currentFormData.value.file = {
    title: file.name,
    url: file.key,
    mimeType: file.mimeType,
  }
  if (canEditInputs.value) {
    updateForm(currentFormData.value)
  }
}

const saveAudioRecording = async (recording: PreSignedFile) => {
  if (!recording)
    return
  currentFormData.value.audio = {
    title: recording.name,
    url: recording.key,
    mimeType: recording.mimeType,
    duration: recording.duration,
    time: recording.duration ? formatSeconds(recording.duration) : undefined,
  }
  if (canEditInputs.value) {
    updateForm(currentFormData.value)
  }
}

const removeDataFile = (key: 'audio' | 'file' | 'image') => {
  if (canEditInputs.value) {
    currentFormData.value[key] = undefined
    updateForm(currentFormData.value)
  }
}

const getVersionName = (answer: AssignmentAnswer) => answer.isDraft
  ? `${answer.version} (${t('formWriter.draft')})`
  : String(answer.version)

const getVersionNameFromId = (versionId: number) => {
  const version = versions.value.find(v => v.version === versionId)
  return version ? getVersionName(version) : String(versionId)
}

const getLatestVersionId = () => versions.value[0]?.version
const selectVersion = (versionId: number | undefined) => selectedVersionId.value = versionId

watch(activeStudent, (student) => {
  if (student && versions.value.length) {
    selectVersion(getLatestVersionId())
  } else {
    selectVersion(undefined)
    resetCurrentFormData()
  }
})

watch(versions, () => {
  const latestVersionId = getLatestVersionId()
  if (!versions.value.length) {
    selectVersion(undefined)
    return
  }

  // Auto-select latest version after save
  if (justSaved.value) {
    justSaved.value = false
    selectVersion(latestVersionId)
    return
  }

  // Keep version selection if it still exists
  if (selectedVersionId.value && versions.value.some(v => v.version === selectedVersionId.value)) {
    return
  }

  // Initially select latest version if no version is selected yet
  if (shouldAutoSelectVersion.value) {
    selectVersion(latestVersionId)
  }
}, { immediate: true })

watch(formData, (data) => {
  if (!data)
    return

  currentFormData.value.image = data.image
  currentFormData.value.audio = data.audio
  currentFormData.value.file = data.file

  if (!currentFormData.value.fields?.length || item.assignment) {
    currentFormData.value.fields = [...(data.fields ?? [])]
  }

  if (isStudent.value) {
    initialFormData.value ??= { ...data }
  }
}, { immediate: true })

watch(imagesOnCanvas, () => {
  if (!imagesOnCanvas.value.length)
    return
  images.value = imagesOnCanvas.value.map(img =>
    getImageSrc((img as ContentImage).image as ImageRelationField, 'thumbnail_large'))
}, { immediate: true })

watch([isFetching, currentFormData], () => {
  if (isFetching.value || !currentFormData.value.image)
    return
  if (!images.value.includes(currentFormData.value.image)) {
    images.value = [currentFormData.value.image, ...images.value]
  }
})
</script>

<template>
  <div class="relative mx-[calc(var(--col-gap)*-1)] space-y-2">
    <StudentFilters
      v-if="showStudentFilters"
      v-model="activeStudent"
      data-ignore-tts
      :item
    />
    <div
      class="relative rounded-xl bg-(--theme-10) text-(--theme-60) md:mt-0 print:mt-0"
      :class="showModelText ? 'p-0' : 'p-4 sm:p-8'"
    >
      <p v-if="isError" v-text="t('formWriter.error')" />
      <FormWriterSkeleton v-else-if="isLoading" />
      <AuCallout
        v-if="!item.methods.length && !isLoading"
        variant="warning"
        class="mb-4"
        :title="t('formWriter.callouts.missingMethods')"
      />
      <AuCallout
        v-if="isStudent && !assignment && !isLoading"
        variant="info"
        class="mb-4"
        dismissible
        :title="t('formWriter.callouts.missingAssignment')"
      />
      <AuCallout
        v-if="assignment && assignment.answer.length >= 100 && !isLoading"
        variant="error"
        class="mb-4"
        :title="t('formWriter.callouts.versionLimitReached')"
      />
      <AuCallout
        v-if="isStudent && isViewingPreviousVersion && !isLoading"
        variant="warning"
        class="mb-4"
        :title="t('formWriter.callouts.viewingPreviousVersion')"
      />
      <div
        v-if="!isLoading && !showModelText"
        class="mb-4"
      >
        <span class="cms-h cms-h3 font-bold" v-text="item.title" />
        <div
          class="prose prose-ul:!m-0 prose-ol:!m-0 text-[1em] text-[currentColor] *:my-2 *:first:mt-0"
          :style="{
            '--tw-prose-bullets': 'var(--theme-60)',
            '--tw-prose-counters': 'var(--theme-60)',
            '--tw-prose-td-borders': 'var(--theme-60)',
            '--tw-prose-th-borders': 'var(--theme-60)',
            '--tw-prose-headings': 'var(--theme-60)',
            '--tw-prose-links': 'var(--theme-60)',
            '--tw-prose-pre-bg': 'var(--theme-60)',
            '--tw-prose-pre-code': 'var(--theme-60)',
            '--tw-prose-quote-borders': 'var(--theme-60)',
            '--tw-prose-quotes': 'var(--theme-60)',
          }"
        >
          <RichTextRenderer
            v-if="item.intro"
            :text="item.intro"
          />
        </div>
      </div>
      <div class="flex flex-col gap-6" data-ignore-tts>
        <label v-if="(isTeacher && activeStudent) || (isStudent && versions.length > 1)" class="flex items-center justify-end gap-4">
          <span class="text-sm font-medium" v-text="t('formWriter.labels.chooseVersion')" />
          <Select
            v-model="selectedVersionId"
            :options="versionOptions"
            :color="themeColor"
            shade="light"
            size="lg"
            :option-label="getVersionNameFromId"
            :placeholder="t('formWriter.labels.chooseVersion')"
            :no-options="t('formWriter.labels.noVersions')"
          />
        </label>
        <template v-if="showFormFields || showImageUpload || modelText">
          <ToolBar
            v-if="showToolBar"
            v-model:show-label="showLabel"
            v-model:show-description="showDescription"
            v-model:show-placeholder="showPlaceholder"
            v-model:show-model-text="showModelText"
            :item
            :has-form="showFormFields"
          />
          <template v-if="!showModelText">
            <ImagesBar
              v-if="showImagesBar"
              v-model:images="images"
              v-model:current-image="currentFormData.image"
              @update:current-image="updateForm(currentFormData)"
            />
            <ImageUpload
              v-if="showImageUpload && canUseInputs"
              :current-image="currentFormData.image"
              :can-edit="canEditInputs"
              :upload-disabled="imageUploadDisabled"
              @upload-image="saveImageUpload"
              @remove-image="removeDataFile('image')"
            />
            <transition-group
              v-if="showFormFields && data?.fields?.length"
              id="form-writer-form"
              appear
              tag="form"
              class="flex flex-col gap-6 rounded-xl bg-(--theme-5) p-3 sm:px-6 sm:py-8"
              :class="hasFormInput ? 'border-(--theme-20)' : 'border-(--theme-5)'"
              enter-active-class="duration-500 ease delay-[calc(0.1s+(0.1s*var(--i)))]"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              @submit.prevent.stop="onSubmit"
            >
              <InputField
                v-for="(field, index) in data.fields"
                :key="field.id"
                v-model="currentFormData.fields[index]"
                :field
                :show-label
                :show-description
                :show-placeholder
                :disable-fields="!canEditInputs"
                :max-words="item.maxWords"
                :style="{ '--i': index }"
                @input="onInput(index, $event.target.value)"
              />
              <div
                v-if="item.exportWord"
                key="form-buttons"
                class="flex flex-row-reverse items-center gap-2"
              >
                <ExportButton
                  :item
                  :fields="currentFormData.fields"
                  :current-image="currentFormData.image"
                  :disabled="!hasFormInput"
                />
              </div>
            </transition-group>
          </template>
          <ModelText
            v-if="showModelText && modelText"
            :content="modelText"
            size="medium"
            class="prose prose-lg first:prose-headings:mt-0 !bg-(--theme-5)"
          />
        </template>
        <template v-if="showAudioRecorder">
          <AudioRecorder
            :file-name="audioName"
            :file-key="currentFormData.audio?.url"
            :mime-type="currentFormData.audio?.mimeType"
            :time="currentFormData?.audio?.time"
            :duration="currentFormData?.audio?.duration"
            :can-edit="canEditInputs"
            @save-recording="saveAudioRecording"
            @cancel-recording="removeDataFile('audio')"
          />
        </template>
        <template v-if="showFileUpload">
          <FileUploader
            :file-title="currentFormData.file?.title"
            :file-key="currentFormData.file?.url"
            :mime-type="currentFormData.file?.mimeType"
            :can-edit="canEditInputs"
            @save-upload="saveFileUpload"
            @remove-upload="removeDataFile('file')"
          />
        </template>
      </div>
      <div
        v-if="showSubmitSection"
        :style="{
          bottom: `${overlayOutletHeight}px`,
        }"
        :class="{
          'sticky rounded-lg bg-(--theme-pop) px-8 py-4': canCreateNewVersion && previousVersion,
        }"
        class="mt-6 flex items-center gap-2"
      >
        <span v-if="canCreateNewVersion && previousVersion">
          {{ t('formWriter.saveReminder2') }}
        </span>
        <span v-else-if="previousVersion">
          {{ t('formWriter.saveConfirmation') }}
        </span>
        <span v-else-if="canCreateNewVersion">
          {{ t('formWriter.saveReminder1') }}
        </span>
        <Button
          v-if="assignment && isStudent"
          size="xl"
          form="form-writer-form"
          :color="themeColor"
          type="submit"
          class="ml-auto"
          :disabled="!canCreateNewVersion"
          :data-pendo="PendoFeature.FormWriter.DeliverStudent"
          @click="saveCurrentVersion"
        >
          {{ t('formWriter.submit') }}
        </Button>
      </div>
    </div>
  </div>
</template>
